import { calculateNights } from '.'

import { getCurrentIntl } from 'i18n/util/currentIntl'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import type {
  SavedAccommodationRentalData,
  SavedHotelData,
} from 'tripPlanner/types/tripItem'
import { displayPricingFromDTO, isTotalMode } from './accommodationPricingDisplay'

interface LuxPlusPricingFlags {
  luxPlusEnabled: boolean
  canViewLuxPlusBenefits: boolean
}

export function getHotelRoomDescription(
  _unit: string = 'room',
  roomName?: string,
  rooms?: Array<App.Occupants>,
) {
  if (rooms && rooms.length > 0 && roomName) {
    const intl = getCurrentIntl()
    return intl.formatMessage({
      id: 'HYvjqjajz30q0Gu8RIcu',
      defaultMessage: '{roomName} ({count, plural, one {# room} other {# rooms}})',
      description: 'Hotel room name with number of rooms on the hotel or room booking page',
    }, { roomName, count: rooms.length })
  }

  return roomName
}

export const getAccommodationPriceAndLabel = (
  offer: App.Offer | App.BedbankOffer,
  savedItemData: SavedHotelData | SavedAccommodationRentalData,
  { luxPlusEnabled, canViewLuxPlusBenefits }: LuxPlusPricingFlags = { luxPlusEnabled: false, canViewLuxPlusBenefits: false },
) => {
  const intl = getCurrentIntl()
  const {
    checkIn,
    checkOut,
  } = savedItemData
  const pricing = 'pricing' in savedItemData ? savedItemData.pricing : undefined
  const displayPricing = pricing ? displayPricingFromDTO(pricing) : undefined
  const isTotalPrice = pricing ? isTotalMode(pricing.mode) : false
  // Rental/villa bookmarks (SavedAccommodationRentalData) never carry a `pricing` DTO —
  // only hotel bookmarks do. Fall back to their flat, already-all-in fields in that case.
  let price = pricing ? displayPricing?.price : savedItemData.price
  let value = pricing ? displayPricing?.value : savedItemData.value
  let luxPlusPrice = pricing
    ? displayPricing?.luxPlusPrice
    : ('luxPlusPrice' in savedItemData ? savedItemData.luxPlusPrice : undefined)
  let isBundleAdjusted = false

  let priceLabel = isTotalPrice
    ? intl.formatMessage({
        id: 'LaX_Mx023lMFcx7IdidA',
        defaultMessage: 'Total',
        description: 'Default total price label for a saved accommodation item in the trip planner',
      })
    : intl.formatMessage({
        id: '2H0-6rsLz3iiIY8MoFQz',
        defaultMessage: 'From',
        description: 'Default price label for a saved accommodation item in the trip planner',
      })

  if (offer.type !== 'bedbank_hotel' && offer.bundledWithFlightsOnly) {
    const durationNights = pricing?.durationNights ?? calculateNights(checkIn, checkOut)
    if (durationNights) {
      priceLabel = isTotalPrice
        ? intl.formatMessage({
            id: 'F2PSPl8vgxkVUAJnd2iR',
            defaultMessage: '{count, plural, one {# night} other {# nights}} total',
            description: 'Duration in nights as selected accommodation total price label in the trip planner',
          }, { count: durationNights })
        : intl.formatMessage({
            id: '-HRXB3KScv8daIFE9bYk',
            defaultMessage: '{count, plural, one {# night} other {# nights}} from',
            description: 'Duration in nights as accommodation price label in the trip planner',
          }, { count: durationNights })
    }

    if (offer.bundledWithFlightsOnly && savedItemData.flightBundle) {
      const savedFlightPrice = savedItemData.flightBundle.savedPrice

      if (
        savedItemData.flightBundle &&
        typeof price === 'number' &&
        typeof savedFlightPrice === 'number'
      ) {
        price += savedFlightPrice
        if (typeof luxPlusPrice === 'number') {
          luxPlusPrice += savedFlightPrice
        }
        if (typeof value === 'number') {
          value += savedFlightPrice
        }
        isBundleAdjusted = true
      } else {
        // If `bundledWithFlightsOnly` is true, we *have* to show the price with flights for contractual reasons.
        // So if we don't have the flight price for whatever reason, then we just can't show the price here.
        price = undefined
        luxPlusPrice = undefined
        value = undefined
      }

      if (durationNights) {
        priceLabel = isTotalPrice
          ? intl.formatMessage({
              id: 'XlD_BhCe-Ewq1Reap1T7',
              defaultMessage: '{count, plural, one {# night} other {# nights}} + flights total',
              description: 'Duration in nights with flights as selected accommodation total price label',
            }, { count: durationNights })
          : intl.formatMessage({
              id: 'QPaXiXH6hY6hSTjzb2yc',
              defaultMessage: '{count, plural, one {# night} other {# nights}} + flights from',
              description: 'Duration in nights with flights as accommodation price label',
            }, { count: durationNights })
      }
    }
  }

  const useMemberDiscount = luxPlusEnabled && canViewLuxPlusBenefits &&
    typeof luxPlusPrice === 'number' && typeof price === 'number' && luxPlusPrice < price

  let discountPercent: number | undefined
  if (!pricing) {
    // Rental/villa bookmarks (and legacy hotel bookmarks saved before `pricing` existed)
    // have no DTO to derive a discount from — fall back to the flat saved field, unless
    // a member price is actually being shown, in which case recompute from it.
    discountPercent = useMemberDiscount && typeof value === 'number' && typeof luxPlusPrice === 'number'
      ? getValueOffPercent(value, luxPlusPrice)
      : savedItemData.discountPercent
  } else if (isBundleAdjusted) {
    // The DTO's discountPercent is hotel-only; once we've added the flight price on top,
    // it no longer reflects the displayed price/value, so recompute it from those.
    const priceForDiscount = useMemberDiscount ? luxPlusPrice : price
    discountPercent = typeof priceForDiscount === 'number' && typeof value === 'number'
      ? getValueOffPercent(value, priceForDiscount)
      : undefined
  } else {
    discountPercent = useMemberDiscount && pricing.memberDiscountPercent != null
      ? pricing.memberDiscountPercent
      : pricing.discountPercent
  }

  return { price, luxPlusPrice, priceLabel, value, discountPercent }
}
